import {
  type ClipboardEvent,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';

interface Props {
  value: string;
  onChange: (digits: string) => void;
  onComplete?: (digits: string) => void;
  length?: number;
  disabled?: boolean;
  error?: string;
  autoFocus?: boolean;
}

/**
 * Six-digit code input with auto-advance + paste support.
 *
 * Behaviour matches PDF spec p20:
 *   - Numeric keypad on mobile (inputMode="numeric")
 *   - Auto-focus moves to the next box after a digit is entered
 *   - Backspace on an empty box jumps back to the previous one
 *   - Pasting "123456" fills all 6 boxes at once
 *   - When all boxes are filled, onComplete fires (lets the parent
 *     auto-submit without a separate verify-button tap)
 *
 * A multi-character value can arrive through TWO different events, and
 * both must fill every box:
 *   - a real `paste` event (desktop Cmd-V, Android long-press → Paste)
 *   - a plain `input` event carrying the whole code — which is what iOS
 *     Safari produces for the QuickType suggestion bar and for
 *     `autocomplete="one-time-code"` SMS autofill. No paste event is
 *     ever dispatched there, so `handleChange` is the only hook.
 */
export function OtpInput({
  value,
  onChange,
  onComplete,
  length = 6,
  disabled,
  error,
  autoFocus = true,
}: Props): React.JSX.Element {
  const { t } = useTranslation();
  const inputs = useRef<(HTMLInputElement | null)[]>([]);
  // Boxes are the source of truth for POSITION; `value` carries only the
  // digits typed so far. Deriving boxes from `value` collapsed interior
  // gaps: clearing the 3rd digit of "123456" joined to "12456", which
  // padDigits then re-spread as 1,2,4,5,6,_ — every later digit shifted
  // left and one was silently lost.
  const [boxes, setBoxes] = useState<string[]>(() => padDigits(value, length));

  // Re-seed when the parent changes the value out from under us (paste,
  // SMS autofill, the debug-code prefill) — but never on our own edits,
  // which would collapse the gaps again. Adjusting state during render is
  // React's documented pattern for "derive from a changed prop"; an effect
  // would render the stale boxes for a frame first.
  const [lastValue, setLastValue] = useState(value);
  if (value !== lastValue) {
    setLastValue(value);
    if (value !== boxes.join('')) setBoxes(padDigits(value, length));
  }

  const digits = boxes;

  useEffect(() => {
    if (autoFocus) inputs.current[0]?.focus();
  }, [autoFocus]);

  const focusBox = useCallback((index: number) => {
    const next = inputs.current[index];
    if (next) {
      next.focus();
      next.select();
    }
  }, []);

  const writeDigit = useCallback(
    (index: number, digit: string) => {
      const next = digits.slice();
      next[index] = digit;
      setBoxes(next);
      // Report the digits typed so far. Gaps simply collapse here — the
      // parent only submits on a full 6-digit code, so a code with a hole
      // can never be sent.
      const joined = next.join('');
      onChange(joined);
      if (digit && index < length - 1) focusBox(index + 1);
      if (isComplete(joined, length)) onComplete?.(joined);
    },
    [digits, length, onChange, onComplete, focusBox],
  );

  /**
   * Write a whole run of digits starting at `start` — the single entry
   * point for every multi-character insertion (clipboard paste, iOS
   * QuickType, SMS autofill, Android autofill). Overflow past the last
   * box is clamped away rather than wrapping, so autofilling from box 4
   * fills 4→6 and stops.
   */
  const fillFrom = useCallback(
    (start: number, incoming: string) => {
      const clean = incoming.replace(/[^0-9]/g, '').slice(0, length - start);
      if (!clean) return;
      const next = digits.slice();
      for (let k = 0; k < clean.length; k += 1) next[start + k] = clean[k];
      setBoxes(next);
      const joined = next.join('');
      onChange(joined);
      focusBox(Math.min(start + clean.length, length - 1));
      if (isComplete(joined, length)) onComplete?.(joined);
    },
    [digits, length, onChange, onComplete, focusBox],
  );

  function handleChange(i: number, raw: string): void {
    const clean = raw.replace(/[^0-9]/g, '');
    // Multi-character insertion. Collapsing it to a single digit here is
    // what dropped 5 of the 6 digits on iOS: autofill and the QuickType
    // bar deliver the whole code through this event, never through
    // `paste`, so the remainder vanished with no error anywhere.
    if (clean.length > 1) {
      fillFrom(i, clean);
      return;
    }
    writeDigit(i, clean.slice(-1)); // typing, overtype, delete
  }

  function handleKey(i: number, e: KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'Backspace' && !digits[i] && i > 0) {
      e.preventDefault();
      focusBox(i - 1);
      writeDigit(i - 1, '');
      return;
    }
    if (e.key === 'ArrowLeft' && i > 0) {
      e.preventDefault();
      focusBox(i - 1);
      return;
    }
    if (e.key === 'ArrowRight' && i < length - 1) {
      e.preventDefault();
      focusBox(i + 1);
    }
  }

  function handlePaste(i: number, e: ClipboardEvent<HTMLInputElement>): void {
    const text = e.clipboardData.getData('text').replace(/[^0-9]/g, '');
    if (!text) return;
    // preventDefault stops the browser also firing an `input` event with
    // the same text, which would run fillFrom twice.
    e.preventDefault();
    fillFrom(i, text);
  }

  return (
    <div className="w-full">
      {/* Boxes flex-shrink on narrow phones (6×48px + gaps overflows a
          360px viewport's 312px content column — Peyman round-2: the OTP
          screen horizontally scrolled on mobile). flex-1 + max-w keeps
          them equal-width, capped at 48px, never overflowing. */}
      <div
        role="group"
        aria-label={t('otp.input')}
        className="flex justify-center gap-1.5 sm:gap-2"
      >
        {Array.from({ length }).map((_, i) => (
          <input
            key={i}
            ref={(el) => {
              inputs.current[i] = el;
            }}
            type="text"
            inputMode="numeric"
            pattern="\d*"
            // On EVERY box, not just the first: Safari only offers the
            // one-time-code fill to the focused field, so a citizen who
            // taps box 3 got nothing at all.
            autoComplete="one-time-code"
            // Must be `length`, not 1. WebKit truncates an inserted or
            // autofilled string to maxLength BEFORE dispatching `input`,
            // so with maxLength=1 handleChange could never observe more
            // than the first character. Typing still yields one digit per
            // box: the input is controlled (`value` is always ≤1 char)
            // and focus selects the box, so a keystroke overtypes.
            maxLength={length}
            disabled={disabled}
            value={digits[i] ?? ''}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKey(i, e)}
            onPaste={(e) => handlePaste(i, e)}
            // Tapping into a filled box must replace, not append — the
            // caret would otherwise sit after the digit and the next
            // keystroke would read as a 2-char "paste".
            onFocus={(e) => e.currentTarget.select()}
            aria-label={`${t('otp.input')} ${i + 1}`}
            className={cn(
              'h-14 min-w-0 max-w-12 flex-1 rounded-lg border bg-card text-center text-2xl font-semibold text-foreground tabular-nums',
              'focus:outline-none focus:ring-2 focus:ring-primary/40',
              error ? 'border-error' : 'border-border',
              disabled && 'cursor-not-allowed opacity-60',
            )}
          />
        ))}
      </div>
      {error && <p className="mt-3 text-sm text-error">{error}</p>}
    </div>
  );
}

/** Every box filled with a digit — `join('')` collapses gaps, so a
 *  string of exactly `length` numerals means no box is empty. */
function isComplete(joined: string, length: number): boolean {
  return joined.length === length && /^\d+$/.test(joined);
}

function padDigits(value: string, length: number): string[] {
  const clean = value.replace(/[^0-9]/g, '').slice(0, length);
  return Array.from({ length }, (_, i) => clean[i] ?? '');
}
